/**
 * Servicio de autenticación para residentes.
 *
 * Este servicio maneja el flujo de autenticación de dos pasos:
 * 1. Solicitar código de verificación por WhatsApp
 * 2. Verificar código y obtener tokens JWT
 * 3. (Opcional) Seleccionar conjunto si el usuario tiene múltiples
 */

import axios from "axios";
import swal from "sweetalert";
import axiosInstance from "./AxiosInstance";

const backendUrl = process.env.REACT_APP_BACKEND;

// Constantes
export const CODE_LENGTH = 6;
export const CODE_EXPIRATION_MINUTES = 5;
export const PHONE_NUMBER_LENGTH = 10;

/**
 * Solicita un código de verificación por WhatsApp.
 *
 * @param {string} celular - Número de celular del residente (10 dígitos)
 * @returns {Promise} - Promesa con la respuesta del servidor
 */
export function solicitarCodigo(celular) {
  const postData = {
    celular: celular,
  };
  return axios.post(
    `${backendUrl}/api/v3/residentes/auth/solicitar-codigo/`,
    postData,
  );
}

/**
 * Verifica el código de verificación y obtiene tokens JWT.
 *
 * Si el número está asociado a múltiples conjuntos, la respuesta incluirá:
 * - requiere_seleccion_conjunto: true
 * - conjuntos_disponibles: array con los conjuntos disponibles
 * - tokens temporales para hacer la selección
 *
 * @param {string} celular - Número de celular del residente
 * @param {string} codigo - Código de verificación recibido por WhatsApp
 * @returns {Promise} - Promesa con la respuesta del servidor
 */
export function verificarCodigo(celular, codigo) {
  const postData = {
    celular: celular,
    codigo: codigo,
  };
  return axios.post(
    `${backendUrl}/api/v3/residentes/auth/verificar-codigo/`,
    postData,
  );
}

/**
 * Cambia al conjunto seleccionado y obtiene nuevos tokens.
 *
 * Este endpoint requiere autenticación JWT (tokens obtenidos de verificarCodigo).
 * Devuelve tokens del usuario asociado al CitofoniaUnion seleccionado.
 *
 * @param {number} conjuntoId - ID del CitofoniaUnion seleccionado
 * @param {string} accessToken - Token de acceso JWT
 * @returns {Promise} - Promesa con nuevos tokens del usuario del conjunto
 */
export function cambiarConjunto(conjuntoId, accessToken) {
  const postData = {
    conjunto_id: conjuntoId,
  };
  return axios.post(
    `${backendUrl}/api/v3/residentes/auth/cambiar-conjunto/`,
    postData,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
}

/**
 * Obtiene la lista de conjuntos disponibles para el usuario autenticado.
 *
 * @param {string} accessToken - Token de acceso JWT
 * @returns {Promise} - Promesa con la lista de conjuntos
 */
export function obtenerMisConjuntos(accessToken) {
  return axios.get(`${backendUrl}/api/v3/residentes/auth/mis-conjuntos/`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Formatea y muestra errores de la API de residentes.
 *
 * @param {object} errorResponse - Respuesta de error del servidor
 * @returns {string} - Mensaje de error formateado
 */
export function formatResidenteError(errorResponse) {
  let errorMessage = "Ha ocurrido un error inesperado.";

  if (errorResponse.message) {
    errorMessage = errorResponse.message;
  } else if (errorResponse.errors && Array.isArray(errorResponse.errors)) {
    // Formato de drf_standardized_errors
    errorMessage = errorResponse.errors[0].detail;
  } else if (errorResponse.celular && Array.isArray(errorResponse.celular)) {
    errorMessage = errorResponse.celular[0];
  } else if (errorResponse.codigo && Array.isArray(errorResponse.codigo)) {
    errorMessage = errorResponse.codigo[0];
  } else if (
    errorResponse.conjunto_id &&
    Array.isArray(errorResponse.conjunto_id)
  ) {
    errorMessage = errorResponse.conjunto_id[0];
  }

  swal("Oops", errorMessage, "error");
  return errorMessage;
}

/**
 * Guarda los tokens del residente en localStorage.
 *
 * @param {object} tokenDetails - Objeto con access, refresh y user
 * @returns {object} - Token details con fecha de expiración
 */
export function saveResidenteTokenInLocalStorage(tokenDetails) {
  const twelveHoursInMilliseconds = 12 * 60 * 60 * 1000;
  const tokenData = {
    access: tokenDetails.access,
    refresh: tokenDetails.refresh,
    user: tokenDetails.user,
    expireDate: new Date().getTime() + twelveHoursInMilliseconds,
  };
  localStorage.setItem("userDetails", JSON.stringify(tokenData));
  return tokenData;
}

/**
 * Guarda tokens temporales para selección de conjunto.
 * Estos tokens son usados temporalmente mientras el usuario selecciona un conjunto.
 *
 * @param {object} tokenDetails - Objeto con access, refresh
 * @returns {object} - Token details temporales
 */
export function saveTempTokensForSelection(tokenDetails) {
  const tempTokenData = {
    access: tokenDetails.access,
    refresh: tokenDetails.refresh,
  };
  localStorage.setItem("tempResidenteTokens", JSON.stringify(tempTokenData));
  return tempTokenData;
}

/**
 * Obtiene los tokens temporales guardados.
 *
 * @returns {object|null} - Tokens temporales o null si no existen
 */
export function getTempTokensForSelection() {
  const tempTokens = localStorage.getItem("tempResidenteTokens");
  return tempTokens ? JSON.parse(tempTokens) : null;
}

/**
 * Elimina los tokens temporales.
 */
export function clearTempTokensForSelection() {
  localStorage.removeItem("tempResidenteTokens");
}

/**
 * Valida el formato del número de celular.
 *
 * @param {string} celular - Número de celular a validar
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export function validarCelular(celular) {
  const celularLimpio = celular.replace(/\D/g, "");

  if (!celularLimpio) {
    return {
      isValid: false,
      error: "El número de celular es requerido",
    };
  }

  if (celularLimpio.length < PHONE_NUMBER_LENGTH) {
    return {
      isValid: false,
      error: `El número de celular debe tener ${PHONE_NUMBER_LENGTH} dígitos`,
    };
  }

  if (celularLimpio.length > 12) {
    return {
      isValid: false,
      error: "El número de celular no puede tener más de 12 dígitos",
    };
  }

  return {
    isValid: true,
    error: null,
    celularLimpio: celularLimpio.slice(-PHONE_NUMBER_LENGTH),
  };
}

/**
 * Valida el formato del código de verificación.
 *
 * @param {string} codigo - Código a validar
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export function validarCodigo(codigo) {
  const codigoLimpio = codigo.replace(/\D/g, "");

  if (!codigoLimpio) {
    return {
      isValid: false,
      error: "El código de verificación es requerido",
    };
  }

  if (codigoLimpio.length !== CODE_LENGTH) {
    return {
      isValid: false,
      error: `El código debe tener ${CODE_LENGTH} dígitos`,
    };
  }

  return {
    isValid: true,
    error: null,
    codigoLimpio: codigoLimpio,
  };
}

/**
 * Obtiene el perfil del residente autenticado desde el backend.
 * Usado para rehidratar el store de Redux en un F5 / reload de página,
 * garantizando que los datos del residente estén siempre frescos.
 *
 * @returns {Promise} - Promesa con { success: true, user: { perfil_residente: {...}, ... } }
 */
export function getMiPerfilResidente() {
  return axiosInstance.get("/api/v3/residentes/auth/me/");
}
